import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import * as reduxApi from 'src/utils/redux-api-v2';
import {camel, snake} from 'src/utils';
import omit from 'lodash/omit';
import {key, fetching} from 'src/utils/redux';
import flow from 'lodash/flow';
import {validatePhone} from 'src/utils/phone';

import type {
  PaginationDetails,
  ContactList,
  CsvMapping,
  APIFilters,
  FileCsvUploadEntries,
  FileUploadAdditionalDetails,
  ContactListFilters,
  ContactDetails,
  EditContactListData,
  AtsContactEntityType,
} from 'src/types/contacts-genesis';
import {selectFileUploadAdditionalDetails} from 'src/selectors/contacts-genesis';


export const SET_CSV_FILE_UPLOAD_HEADERS = 'contacts/setCsvFileUploadHeaders';
export const SET_CSV_FILE_UPLOAD_ENTRIES = 'contacts/setCsvFileUploadEntries';
export const SET_CSV_FILE_UPLOAD = 'contacts/setCsvFileUpload';
export const SET_FILE_UPLOAD_ADDITIONAL_DETAILS =
  'contacts/setFileUploadAdditionalDetails';
export const SET_PAGINATION_DATA = 'contacts/setPaginationData';
export const RESET_PAGINATION_DATA = 'contacts/resetPaginationData';
export const SET_CONTACT_LISTS_DATA = 'contacts/setContactListData';
export const SET_ARCHIVED_CONTACT_LISTS_DATA =
  'contacts/setArchivedContactListData';
export const SET_CONTACT_LISTS_FOR_UPLOAD = 'contacts/setContactListsForUpload';
export const UPDATE_CONTACT_LIST_LIMIT_REACHED =
  'contacts/updateContactListLimitReached';
export const SET_CONTACT_LISTS_FILTER = 'contacts/setContactListsFilter';
export const UPDATE_CONTACT_LIST_DATA = 'contacts/updateContactListData';
export const SET_EDIT_CONTACT_LISTS_DATA = 'contacts/setEditContactListsData';
export const REMOVE_CONTACT_LIST_FROM_STATE =
  'contacts/removeContactListFromState';
export const SET_CONTACT_PHONE_NUMBER = 'contacts/setContactPhoneNumber';
export const SET_CONTACT_LIST_ID_FOR_CONTACT_CREATION =
  'contacts/setContactListIdForContactCreation';

export type ContactsGenesisActions =
  | SetCsvFileUploadHeadersAction
  | SetCsvFileUploadEntriesAction
  | SetPaginationDataAction
  | SetContactListsFilterAction
  | SetCsvFileUploadAction
  | SetFileUploadAdditionalDetailsAction
  | SetContactListsDataAction
  | UpdateContactListsDataAction
  | SetArchivedContactListsDataAction
  | SetContactListsForUploadAction
  | SetContactPhoneNumberAction
  | SetContactListIdForContactCreationAction
  | SetEditContactListsDataAction
  | ResetPaginationDataAction
  | RemoveContactListFromStateAction;

export type SetContactPhoneNumberAction = {
  type: typeof SET_CONTACT_PHONE_NUMBER;
  payload: string;
};

export const setContactPhoneNumber = (
  payload: string,
): SetContactPhoneNumberAction => ({
  type: SET_CONTACT_PHONE_NUMBER,
  payload,
});

export type SetContactListIdForContactCreationAction = {
  type: typeof SET_CONTACT_LIST_ID_FOR_CONTACT_CREATION;
  payload: string;
};

export const setContactListIdForContactCreation = (
  payload: string,
): SetContactListIdForContactCreationAction => ({
  type: SET_CONTACT_LIST_ID_FOR_CONTACT_CREATION,
  payload,
});

export type SetContactListsFilterAction = {
  type: typeof SET_CONTACT_LISTS_FILTER;
  payload: ContactListFilters;
};

export const setContactListsFilter = (
  payload: ContactListFilters,
): SetContactListsFilterAction => ({
  type: SET_CONTACT_LISTS_FILTER,
  payload,
});

export type SetCsvFileUploadHeadersAction = {
  type: typeof SET_CSV_FILE_UPLOAD_HEADERS;
  payload: string[];
};

export const setCsvFileUploadHeaders = (
  payload: string[],
): SetCsvFileUploadHeadersAction => ({
  type: SET_CSV_FILE_UPLOAD_HEADERS,
  payload,
});

export type SetCsvFileUploadEntriesAction = {
  type: typeof SET_CSV_FILE_UPLOAD_ENTRIES;
  payload: FileCsvUploadEntries[];
};

export const setCsvFileUploadEntries = (
  payload: FileCsvUploadEntries[],
): SetCsvFileUploadEntriesAction => ({
  type: SET_CSV_FILE_UPLOAD_ENTRIES,
  payload,
});

export type SetCsvFileUploadAction = {
  type: typeof SET_CSV_FILE_UPLOAD;
  payload: File | null;
};

export const setCsvFileUpload = (
  payload: File | null,
): SetCsvFileUploadAction => ({
  type: SET_CSV_FILE_UPLOAD,
  payload,
});

export type SetFileUploadAdditionalDetailsAction = {
  type: typeof SET_FILE_UPLOAD_ADDITIONAL_DETAILS;
  payload: FileUploadAdditionalDetails;
};

export const setFileUploadAdditionalDetails = (
  payload: FileUploadAdditionalDetails,
): SetFileUploadAdditionalDetailsAction => ({
  type: SET_FILE_UPLOAD_ADDITIONAL_DETAILS,
  payload,
});

export type SetPaginationDataAction = {
  type: typeof SET_PAGINATION_DATA;
  payload: {
    key: string;
    paginationDetails: PaginationDetails;
  };
};

export const setPaginationData = (
  key: string,
  paginationDetails: PaginationDetails,
): SetPaginationDataAction => ({
  type: SET_PAGINATION_DATA,
  payload: {key, paginationDetails},
});

export type ResetPaginationDataAction = {
  type: typeof RESET_PAGINATION_DATA;
};

export const resetPaginationData = (): ResetPaginationDataAction => ({
  type: RESET_PAGINATION_DATA,
});

export type SetContactListsDataAction = {
  type: typeof SET_CONTACT_LISTS_DATA;
  payload: {
    contactLists: ContactList[];
    update: boolean;
  };
};

export const setContactListsData = (payload: {
  contactLists: ContactList[];
  update: boolean;
}): SetContactListsDataAction => ({
  type: SET_CONTACT_LISTS_DATA,
  payload,
});

export type SetEditContactListsDataAction = {
  type: typeof SET_EDIT_CONTACT_LISTS_DATA;
  payload: EditContactListData;
};

export const setEditContactListsData = (
  payload: EditContactListData,
): SetEditContactListsDataAction => ({
  type: SET_EDIT_CONTACT_LISTS_DATA,
  payload,
});

export type UpdateContactListsDataAction = {
  type: typeof UPDATE_CONTACT_LIST_DATA;
  payload: {
    id: string;
    starred: boolean;
  };
};

export const updateContactListsData = (payload: {
  id: string;
  starred: boolean;
}): UpdateContactListsDataAction => ({
  type: UPDATE_CONTACT_LIST_DATA,
  payload,
});

export type SetArchivedContactListsDataAction = {
  type: typeof SET_ARCHIVED_CONTACT_LISTS_DATA;
  payload: {
    contactLists: ContactList[];
    update: boolean;
  };
};

export const setArchivedContactListsData = (payload: {
  contactLists: ContactList[];
  update: boolean;
}): SetArchivedContactListsDataAction => ({
  type: SET_ARCHIVED_CONTACT_LISTS_DATA,
  payload,
});

export type SetContactListsForUploadAction = {
  type: typeof SET_CONTACT_LISTS_FOR_UPLOAD;
  payload: ContactList[];
};

export const setContactListsForUpload = (
  payload: ContactList[],
): SetContactListsForUploadAction => ({
  type: SET_CONTACT_LISTS_FOR_UPLOAD,
  payload,
});

export type RemoveContactListFromStateAction = {
  type: typeof REMOVE_CONTACT_LIST_FROM_STATE;
  payload: string;
};

export const removeContactListFromState = (
  payload: string,
): RemoveContactListFromStateAction => ({
  type: REMOVE_CONTACT_LIST_FROM_STATE,
  payload,
});

export const getContacts =
  ({
    page,
    pageSize,
    searchTerm,
    sortBy,
    order,
    shareType,
  }: APIFilters): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      const url = searchTerm
        ? `messages_v2/contacts/search/${searchTerm}`
        : 'messages_v2/contacts';
      const result = await dispatch(
        reduxApi.get(url, snake({pageSize, page, sortBy, order, shareType}), {
          apiPath: '/api/v2/',
        }),
      );
      return camel(result.contacts);
    } catch (err: any) {
      throw err;
    }
  };

export const getContactsForContactList =
  ({
    contactListId,
    page,
    pageSize,
    searchTerm,
    sortBy,
    order,
    shareType,
  }: APIFilters): ThunkAction =>
  async (dispatch: Dispatch) => {
    if (!contactListId) {
      return;
    }
    try {
      const url = searchTerm
        ? `messages_v2/contacts/search/${searchTerm}`
        : `messages_v2/contact_list/${contactListId}/contacts`;
      const apiParams = {
        pageSize,
        page,
        sortBy,
        order,
        shareType,
        contactListId,
      } as const;
      const modifiedPayload = searchTerm
        ? apiParams
        : omit(apiParams, 'contactListId');
      const result = await dispatch(
        reduxApi.get(url, snake(modifiedPayload), {
          apiPath: '/api/v2/',
        }),
      );
      dispatch(setPaginationData('contacts', {totalPages: result.pages}));
      return camel(result.contacts);
    } catch (err: any) {
      throw err;
    }
  };

export const getRecentlyMessagedContacts =
  ({page, pageSize, searchTerm, sortBy, order}: APIFilters): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      const url = searchTerm
        ? `messages_v2/contacts/search/${searchTerm}`
        : 'messages_v2/contacts';
      const result = await dispatch(
        reduxApi.get(
          url,
          snake({
            pageSize,
            page,
            recentlyMessagedOnly: true,
            sortBy,
            order,
          }),
          {
            apiPath: '/api/v2/',
          },
        ),
      );
      return camel(result.contacts);
    } catch (err: any) {
      throw err;
    }
  };

export const getContactListsToShowInUpload =
  (): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
    try {
      const result = await dispatch(
        reduxApi.get(
          'messages_v2/contact_lists',
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
      if (result) {
        const response = camel(result);
        const contactLists = camel(response.contactLists);
        dispatch(setContactListsForUpload(contactLists));
      }
    } catch (err: any) {
      throw err;
    }
  };

export const getContactLists: (arg1: APIFilters) => ThunkAction = flow(
  key(() => 'contacts-genesis/messages_v2/contact_lists'),
  fetching(),
)(
  ({
      // @ts-expect-error - TS7031 - Binding element 'page' implicitly has an 'any' type.
      page,
      // @ts-expect-error - TS7031 - Binding element 'pageSize' implicitly has an 'any' type.
      pageSize,
      // @ts-expect-error - TS7031 - Binding element 'searchTerm' implicitly has an 'any' type.
      searchTerm,
      // @ts-expect-error - TS7031 - Binding element 'order' implicitly has an 'any' type.
      order,
      // @ts-expect-error - TS7031 - Binding element 'sortBy' implicitly has an 'any' type.
      sortBy,
      // @ts-expect-error - TS7031 - Binding element 'shareType' implicitly has an 'any' type.
      shareType,
      // @ts-expect-error - TS7031 - Binding element 'agentId' implicitly has an 'any' type.
      agentId,
      // @ts-expect-error - TS7031 - Binding element 'includeGroups' implicitly has an 'any' type.
      includeGroups,
      // @ts-expect-error - TS7031 - Binding element 'appendData' implicitly has an 'any' type.
      appendData,
    }) =>
    async (dispatch: Dispatch) => {
      try {
        const url = searchTerm
          ? `messages_v2/contact_lists/search/${searchTerm}`
          : 'messages_v2/contact_lists';

        const omitFilters: Array<string> = [];
        if (shareType === 'all') {
          omitFilters.push('shareType');
        }
        if (agentId === 'all') {
          omitFilters.push('agentId');
        }
        if (includeGroups && includeGroups.length < 1) {
          omitFilters.push('includeGroups');
        }

        const payload = {
          pageSize,
          page,
          order,
          sortBy,
          shareType,
          agentId,
          includeGroups,
        } as const;
        const modifiedPayload = omit(payload, omitFilters);
        const result = await dispatch(
          reduxApi.get(url, snake(modifiedPayload), {
            apiPath: '/api/v2/',
          }),
        );
        const response = camel(result);
        dispatch(
          setPaginationData('contactsList', {
            totalPages: response.pages,
            currentPage: page,
          }),
        );
        const update = appendData && page !== 1;
        dispatch(
          setContactListsData({contactLists: response.contactLists, update}),
        );
      } catch (err: any) {
        throw err;
      }
    },
);

export const getArchivedContactLists =
  ({page, pageSize, searchTerm, sortBy, order}: APIFilters): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      const url = searchTerm
        ? `messages_v2/contact_lists/search/${searchTerm}`
        : 'messages_v2/contact_lists';
      const result = await dispatch(
        reduxApi.get(
          url,
          snake({
            pageSize,
            page,
            sortBy,
            order,
            includeArchived: true,
          }),
          {
            apiPath: '/api/v2/',
          },
        ),
      );
      const response = camel(result);
      dispatch(setPaginationData('archivedList', {totalPages: response.pages}));
      dispatch(
        setArchivedContactListsData({
          contactLists: response.contactLists,
          update: false,
        }),
      );
    } catch (err: any) {
      throw err;
    }
  };

export const getContactsForAContactList =
  ({page, pageSize, searchTerm}: APIFilters): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      const url = searchTerm
        ? `messages_v2/contact_lists/search/${searchTerm}`
        : 'messages_v2/contact_lists';
      const result = await dispatch(
        reduxApi.get(url, snake({pageSize, page}), {
          apiPath: '/api/v2/',
        }),
      );
      const response = camel(result);
      dispatch(setPaginationData('contacts', {totalPages: response.pages}));
      return response;
    } catch (err: any) {
      throw err;
    }
  };

export const getContactListDetails =
  (id: string): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    try {
      const response = await dispatch(
        reduxApi.get(
          `messages_v2/contact_list/${id}`,
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
      return camel(response);
    } catch (err: any) {
      throw err;
    }
  };

export const createContactList =
  (fileId: string): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    const details = selectFileUploadAdditionalDetails(getState());
    const {name, description, shareType} = details;
    const contactListIds: Array<never> = [];
    const fileIds = [fileId];
    const formattedPayload = {
      name,
      description,
      shareType,
      contactListIds,
      fileIds,
    } as const;
    try {
      await dispatch(
        reduxApi.post(
          'messages_v2/contact_list',
          snake(formattedPayload),
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
    } catch (err: any) {
      throw err;
    }
  };

export const updateContactList =
  (file: File, CsvMapping: CsvMapping): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    const formData = new FormData();
    const mapping = snake(CsvMapping);
    formData.append('contact_upload_file', file);
    formData.append('csv_field_map', JSON.stringify(mapping));
    const details = selectFileUploadAdditionalDetails(getState());
    const {contactListId} = details;
    try {
      await dispatch(
        reduxApi.put(
          `messages_v2/contact_list/${contactListId}`,
          formData,
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
    } catch (err: any) {
      throw err;
    }
  };

export const addContactsToList =
  (
    contactListId: string,
    contactIds: string,
    atsEntities?: AtsContactEntityType[],
  ): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      await dispatch(
        reduxApi.put(
          `messages_v2/contact_list/${contactListId}`,
          snake({contactIds, atsEntities}),
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
    } catch (err: any) {
      throw err;
    }
  };

export const createContactListFromContacts =
  (
    name: string,
    description: string,
    shareType: string,
    contactIds: string,
  ): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    try {
      const response = await dispatch(
        reduxApi.post(
          'messages_v2/contact_list',
          snake({name, description, shareType, contactIds}),
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
    } catch (err: any) {
      throw err;
    }
  };

export const archiveContactLists =
  (contactListIds: string): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    try {
      await dispatch(
        reduxApi.put(
          `messages_v2/contact_list`,
          snake({contactListIds, archive: true}),
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
    } catch (err: any) {
      throw err;
    }
  };

export const unarchiveContactLists =
  (contactListIds: string): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    try {
      await dispatch(
        reduxApi.put(
          `messages_v2/contact_list`,
          snake({contactListIds, archive: false}),
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
    } catch (err: any) {
      throw err;
    }
  };

export const deleteContactLists =
  (contactListIds: string): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    try {
      await dispatch(
        reduxApi.put(
          `messages_v2/contact_list`,
          snake({contactListIds, delete: true}),
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
    } catch (err: any) {
      throw err;
    }
  };

export const starContactLists =
  (contactListId: string, starred: boolean): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    try {
      await dispatch(
        reduxApi.put(
          `messages_v2/contact_list/${contactListId}`,
          {starred},
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
      dispatch(updateContactListsData({id: contactListId, starred}));
    } catch (err: any) {
      throw err;
    }
  };

export const addContactToContactList =
  (contactListId: string, data: ContactDetails): ThunkAction<void> =>
  async (dispatch: Dispatch) => {
    const params = omit(data, 'permissionType');
    await dispatch(
      reduxApi.post(
        `messages_v2/contact_list/${contactListId}/contact`,
        snake(params),
        {},
        {apiPath: '/api/v2/'},
      ),
    );
  };

export const updateTitleAndDescOfContactLists =
  (contactListId: string, name: string, description: string): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    try {
      await dispatch(
        reduxApi.put(
          `messages_v2/contact_list/${contactListId}`,
          {name, description},
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
      // update the value in store for the list id - so it will be reflected
    } catch (err: any) {
      throw err;
    }
  };

export const uploadCsvFile =
  (file: File, CsvMapping: CsvMapping): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    const details = selectFileUploadAdditionalDetails(getState());
    const {actionType} = details;
    const formData = new FormData();
    const mapping = snake(CsvMapping);
    formData.append('contact_upload_file', file);
    formData.append('csv_field_map', JSON.stringify(mapping));
    actionType === 'createList' &&
      formData.append('create_contacts', JSON.stringify(false));

    try {
      if (actionType === 'addToExistingList') {
        await dispatch(updateContactList(file, CsvMapping));
        return;
      }
      const response = await dispatch(
        reduxApi.post(
          'messages_v2/contacts/file/upload',
          formData,
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
      if (actionType === 'createList') {
        await dispatch(createContactList(response.id));
      }
    } catch (err: any) {
      throw err;
    }
  };

export const searchContacts =
  (text: string): ThunkAction<unknown> =>
  async (dispatch: Dispatch) => {
    const response = await dispatch(
      reduxApi.get(
        `messages_v2/contacts/search/${text}`,
        {},
        {apiPath: '/api/v2/'},
      ),
    );
    return camel(response);
  };

export const getContact =
  (id: string): ThunkAction<unknown> =>
  async (dispatch: Dispatch) => {
    const response = await dispatch(
      reduxApi.get(`messages_v2/contact/${id}`, {}, {apiPath: '/api/v2/'}),
    );
    return camel(response);
  };

export const createContact =
  (data: ContactDetails): ThunkAction<unknown> =>
  async (dispatch: Dispatch) => {
    const params = snake(data);
    const response = await dispatch(
      reduxApi.post('messages_v2/contact', params, {}, {apiPath: '/api/v2/'}),
    );
    return camel(response);
  };

export const updateContact =
  (contactId: string, data: ContactDetails): ThunkAction<unknown> =>
  async (dispatch: Dispatch) => {
    const modifiedPayload = omit(data, ['permissionType', 'sharedGroupIds']);
    const params = snake(modifiedPayload);
    const response = await dispatch(
      reduxApi.put(
        `messages_v2/contact/${contactId}`,
        params,
        {},
        {apiPath: '/api/v2/'},
      ),
    );
    return camel(response);
  };

export const deleteContact =
  (id: string): ThunkAction<unknown> =>
  async (dispatch: Dispatch) => {
    try {
      return await dispatch(
        reduxApi.del(`messages_v2/contact/${id}`, {}, {apiPath: '/api/v2/'}),
      );
    } catch (error: any) {
      throw error;
    }
  };

export const deleteContacts =
  (contactIds: string): ThunkAction<unknown> =>
  async (dispatch: Dispatch) => {
    try {
      return await dispatch(
        reduxApi.del(`messages_v2/contacts`, snake({contactIds}), {
          apiPath: '/api/v2/',
        }),
      );
    } catch (error: any) {
      throw error;
    }
  };

export const removeContactsFromList =
  (contactListId: string, contactIds: string): ThunkAction<unknown> =>
  async (dispatch: Dispatch) => {
    try {
      return await dispatch(
        reduxApi.put(
          `messages_v2/contact_list/${contactListId}/contacts`,
          snake({contactIds, action: 'remove'}),
          {},
          {
            apiPath: '/api/v2/',
          },
        ),
      );
    } catch (error: any) {
      throw error;
    }
  };

export const getSecurityGroups =
  (): ThunkAction<unknown> => async (dispatch: Dispatch) => {
    try {
      const result = await dispatch(reduxApi.get('security/groups'));
      return result;
    } catch (e: any) {
      throw e;
    }
  };

export const getATSAndNonATSContacts =
  (searchTerm: string): ThunkAction<unknown> =>
  async (dispatch: Dispatch) => {
    try {
      const result = await dispatch(
        reduxApi.get(
          'messages_v2/typeahead',
          {
            limit: 20,
            offset: 0,
            search_string: searchTerm,
            should_include_lists: false,
          },
          {
            apiPath: '/api/v2/',
          },
        ),
      );
      const {contact_data, entities_data} = result;

      // send only valid phone number contacts to the component and only required fields
      const contactsData = contact_data
        // @ts-expect-error - TS7031 - Binding element 'phone_number' implicitly has an 'any' type.
        .filter(({phone_number}) => Boolean(validatePhone(phone_number)))
        // @ts-expect-error - TS7031 - Binding element 'full_name' implicitly has an 'any' type. | TS7031 - Binding element 'first_name' implicitly has an 'any' type. | TS7031 - Binding element 'phone_number' implicitly has an 'any' type. | TS7031 - Binding element 'id' implicitly has an 'any' type.
        .map(({full_name, first_name, phone_number, id}) => {
          return {
            fullName: full_name || first_name || phone_number,
            phoneNumber: phone_number,
            id,
          };
        });
      const entitiesData = entities_data.entities
        // @ts-expect-error - TS7031 - Binding element 'primary_phone' implicitly has an 'any' type.
        .filter(({primary_phone}) => Boolean(validatePhone(primary_phone)))
        // @ts-expect-error - TS7031 - Binding element 'full_name' implicitly has an 'any' type. | TS7031 - Binding element 'first_name' implicitly has an 'any' type. | TS7031 - Binding element 'primary_phone' implicitly has an 'any' type. | TS7031 - Binding element 'id' implicitly has an 'any' type. | TS7031 - Binding element 'entity_type' implicitly has an 'any' type.
        .map(({full_name, first_name, primary_phone, id, entity_type}) => {
          return {
            fullName: full_name || first_name || primary_phone,
            phoneNumber: primary_phone,
            entityType: entity_type,
            id,
          };
        });
      return [...contactsData, ...entitiesData];
    } catch (e: any) {
      throw e;
    }
  };
